import React, { useEffect, useState } from 'react';

import * as styles from './StudyReference.module.css';

import { Reference } from './StudyTypes';
import { ReferenceDocuments } from './ReferenceDocuments';
import { Document } from 'types/graphql';
import { LibkeyLink } from 'pages/StudyList/StudyListItem/LibkeyLink';
import { LibkeyAlertBanner } from 'pages/StudyList/StudyListItem/LibkeyAlertBanner';

type StudyReferenceProps = Reference & {
  /**
   * Whether the parent study has more than one reference. Multi-reference
   * studies show the LibKey alert banner above each flagged reference here;
   * single-reference studies show it at the top of the card instead.
   */
  isMultiReference: boolean;
};

export function StudyReference(props: StudyReferenceProps) {
  const {
    id,
    authors,
    title,
    source,
    journalInfo,
    publisherInfo,
    isMultiReference,
  } = props;

  // Own the reference's documents here so both the uploaded-files panel and the
  // LibKey link react to uploads/deletions. Re-sync if the reference is
  // refetched upstream.
  const [documents, setDocuments] = useState<Document[]>(props.documents);
  useEffect(() => setDocuments(props.documents), [props.documents]);

  // Once the full text has been uploaded the user has done the job the LibKey
  // link points them at, so hide it. A reference URL is not an uploaded file.
  const hasUploadedFullText = documents.length > 0;

  const renderSource = () => {
    if (source) {
      return <p>{source}</p>;
    } else {
      return (
        <>
          <p>{journalInfo}</p>
          <p>{publisherInfo}</p>
        </>
      );
    }
  };

  return (
    <div className={styles.studyReference} data-testid="reference">
      {isMultiReference && (
        <LibkeyAlertBanner referenceId={id} className={styles.alertBanner} />
      )}
      <div className={styles.authors}>{authors || ''}</div>
      <h2 className={styles.title}>{title}</h2>

      <div className={styles.source}>{renderSource()}</div>

      <LibkeyLink
        referenceId={id}
        className={styles.libkeyLink}
        hasUploadedFullText={hasUploadedFullText}
      />

      <div className={styles.tools}>
        <ReferenceDocuments
          {...props}
          documents={documents}
          setDocuments={setDocuments}
        />
      </div>
    </div>
  );
}
